import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2, Send, BookOpen, CalendarDays } from 'lucide-react';
import { z } from 'zod';

import { postContentCompletionRequest } from '@/entities/course';
import { usersQueries } from '@/entities/user';
import { resolveErrorMessage } from '@/shared/lib/errors';
import { cn } from '@/shared/utils';
import { useToast } from '@/shared/ui/toast-provider';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/ui/card';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/shared/ui/empty';
import { Label } from '@/shared/ui/label';
import { Skeleton } from '@/shared/ui/skeleton';
import { Textarea } from '@/shared/ui/textarea';
import { Calendar } from '@/shared/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { useCaptureAnalyticsEvent } from '@/shared/analytics/events';

const completionRequestSchema = z.object({
  selectedContents: z
    .array(z.string())
    .min(1, 'Select at least one course to submit a request.'),
  completionDates: z.record(
    z.string().min(1, 'Please choose when you finished this course.'),
  ),
  additionalInfo: z
    .string()
    .max(500, 'Additional information must be 500 characters or less.')
    .optional(),
});

type CompletionRequestFormValues = z.infer<typeof completionRequestSchema>;

interface CompletionRequestFormErrors {
  selectedContents?: string;
  completionDates: Record<string, string>;
}

const initialFormErrors: CompletionRequestFormErrors = {
  completionDates: {},
};

const convertIssuesToErrors = (
  issues: z.ZodIssue[],
): CompletionRequestFormErrors => {
  const next: CompletionRequestFormErrors = {
    completionDates: {},
  };

  issues.forEach((issue) => {
    const [field, key] = issue.path;
    if (field === 'selectedContents') {
      next.selectedContents = issue.message;
      return;
    }
    if (field === 'completionDates' && typeof key === 'string') {
      next.completionDates[key] = issue.message;
    }
  });

  return next;
};

const mergeCompletionDates = (
  selectedIds: string[],
  completionDates: Record<string, string>,
) =>
  selectedIds.reduce<Record<string, string>>((acc, id) => {
    acc[id] = completionDates[id] ?? '';
    return acc;
  }, {});

const buildIsoDate = (date: string) =>
  new Date(`${date}T00:00:00`).toISOString();

export interface CourseContentOption {
  id: string;
  title: string;
  courseTitle: string;
  courseId: string;
}

export interface CompletionRequestFormProps {
  courseContentOptions: CourseContentOption[];
  isCoursesLoading: boolean;
  coursesError?: string;
}

export const CompletionRequestForm = ({
  courseContentOptions,
  isCoursesLoading,
  coursesError,
}: CompletionRequestFormProps) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const captureAnalyticsEvent = useCaptureAnalyticsEvent();
  const [selectedContents, setSelectedContents] = useState<string[]>([]);
  const [completionDates, setCompletionDates] = useState<
    Record<string, string>
  >({});
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [activeCalendarId, setActiveCalendarId] = useState<string | null>(null);
  const [formErrors, setFormErrors] =
    useState<CompletionRequestFormErrors>(initialFormErrors);

  const completionDatesInput = mergeCompletionDates(
    selectedContents,
    completionDates,
  );

  const validationInput: CompletionRequestFormValues = {
    selectedContents,
    completionDates: completionDatesInput,
    additionalInfo: additionalInfo.trim() || undefined,
  };

  const isFormValid =
    completionRequestSchema.safeParse(validationInput).success;

  const createCompletionRequest = useMutation({
    mutationFn: postContentCompletionRequest,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [...usersQueries.all, 'completion-requests'],
      });
      if (selectedContents.length) {
        const selectedOptions = courseContentOptions.filter((option) =>
          selectedContents.includes(option.id),
        );
        const courseIds = Array.from(
          new Set(selectedOptions.map((option) => option.courseId)),
        );
        captureAnalyticsEvent('course_completion_request_submitted', {
          course_ids: courseIds,
          content_ids: selectedContents,
          total_items: selectedContents.length,
          additional_information: Boolean(additionalInfo.trim()),
        });
      }
      toast({
        type: 'success',
        title: 'Completion request submitted',
        description:
          'We will review and update your course completion shortly.',
      });
      resetForm();
    },
    onError: (error) => {
      toast({
        type: 'error',
        title: 'Unable to submit request',
        description: resolveErrorMessage(
          error,
          'Please review your request and try again.',
        ),
      });
    },
  });

  const handleToggleContent = useCallback((contentId: string) => {
    setSelectedContents((prev) => {
      if (prev.includes(contentId)) {
        setActiveCalendarId((current) =>
          current === contentId ? null : current,
        );
        setCompletionDates((prevDates) => {
          const { [contentId]: _removed, ...rest } = prevDates;
          return rest;
        });

        setFormErrors((prevErrors) => {
          if (
            !prevErrors.selectedContents &&
            !prevErrors.completionDates[contentId]
          ) {
            return prevErrors;
          }

          const nextCompletionDates = { ...prevErrors.completionDates };
          delete nextCompletionDates[contentId];

          return {
            selectedContents: undefined,
            completionDates: nextCompletionDates,
          };
        });

        return prev.filter((id) => id !== contentId);
      }

      setFormErrors((prevErrors) => {
        if (!prevErrors.selectedContents) {
          return prevErrors;
        }
        return {
          ...prevErrors,
          selectedContents: undefined,
        };
      });

      return [...prev, contentId];
    });
  }, []);

  const handleDateChange = useCallback((contentId: string, value: string) => {
    setCompletionDates((prev) => ({
      ...prev,
      [contentId]: value,
    }));

    setFormErrors((prevErrors) => {
      if (!prevErrors.completionDates[contentId]) {
        return prevErrors;
      }

      const nextCompletionDates = { ...prevErrors.completionDates };
      delete nextCompletionDates[contentId];

      return {
        ...prevErrors,
        completionDates: nextCompletionDates,
      };
    });
  }, []);

  const resetForm = useCallback(() => {
    setSelectedContents([]);
    setCompletionDates({});
    setAdditionalInfo('');
    setFormErrors(initialFormErrors);
    setActiveCalendarId(null);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedContents([]);
    setCompletionDates({});
    setFormErrors(initialFormErrors);
    setActiveCalendarId(null);
  }, []);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    async (event) => {
      event.preventDefault();

      const parsed = completionRequestSchema.safeParse(validationInput);

      if (!parsed.success) {
        setFormErrors(convertIssuesToErrors(parsed.error.issues));
        return;
      }

      if (!courseContentOptions?.length) {
        toast({
          type: 'error',
          title: 'No available courses',
          description:
            'We could not find any courses eligible for completion requests.',
        });
        return;
      }

      const items = parsed.data.selectedContents
        .map((contentId) => {
          const option = courseContentOptions.find(
            (item) => item.id === contentId,
          );
          if (!option) {
            return null;
          }
          const claimedDate = parsed.data.completionDates[contentId];
          if (!claimedDate) {
            return null;
          }

          return {
            content: {
              id: option.id,
              title: option.title,
            },
            course: {
              id: option.courseId,
              title: option.courseTitle,
            },
            claimed_completion_datetime: buildIsoDate(claimedDate),
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      if (!items.length) {
        toast({
          type: 'error',
          title: 'No courses selected',
          description: 'Please choose at least one course to continue.',
        });
        return;
      }

      try {
        await createCompletionRequest.mutateAsync({
          items,
          additional_information: parsed.data.additionalInfo ?? '',
        });
      } catch {
        setFormErrors(initialFormErrors);
      }
    },
    [courseContentOptions, createCompletionRequest, toast, validationInput],
  );

  const selectedCount = selectedContents.length;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card className="border border-border/60 shadow-sm">
        <CardHeader className="space-y-3 p-5 pb-5">
          <CardTitle className="text-lg font-semibold text-foreground">
            Request Course Completion
          </CardTitle>
          <CardDescription className="text-sm leading-relaxed text-muted-foreground">
            If your course completion hasn&apos;t been properly recorded, you
            can request that it be marked as completed. Select the courses below
            and let us know when you finished them.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 p-5 pt-0">
          <section className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">
                  Select Courses
                  <span className="ml-1 text-destructive">*</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Completion date required for each selected course.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {selectedCount > 0 ? (
                  <>
                    <Badge className="rounded-full border border-border/50 bg-muted px-3 py-1 text-xs font-semibold">
                      {selectedCount}{' '}
                      {selectedCount === 1 ? 'course' : 'courses'} selected
                    </Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={handleClearSelection}
                    >
                      Clear
                    </Button>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    No courses selected yet.
                  </span>
                )}
              </div>
            </div>

            {formErrors.selectedContents ? (
              <Alert variant="destructive">
                <AlertTitle>Selection required</AlertTitle>
                <AlertDescription>
                  {formErrors.selectedContents}
                </AlertDescription>
              </Alert>
            ) : null}

            {isCoursesLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    className="rounded-2xl border border-dashed border-border/50 bg-muted/20 p-4 shadow-sm"
                  >
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="mt-2.5 h-3 w-1/2" />
                  </div>
                ))}
              </div>
            ) : coursesError ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to load courses</AlertTitle>
                <AlertDescription>{coursesError}</AlertDescription>
              </Alert>
            ) : !courseContentOptions.length ? (
              <div className="rounded-2xl border border-dashed border-border/60 bg-muted/10 p-10 text-center">
                <Empty className="border-none p-0">
                  <EmptyHeader className="gap-3">
                    <EmptyMedia variant="icon">
                      <BookOpen className="h-9 w-9 text-muted-foreground" />
                    </EmptyMedia>
                    <EmptyTitle>No eligible content</EmptyTitle>
                    <EmptyDescription>
                      We could not find any completed lessons linked to your
                      account.
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              </div>
            ) : (
              <div className="space-y-3">
                {courseContentOptions.map((content) => {
                  const isSelected = selectedContents.includes(content.id);
                  const completionDate = completionDates[content.id] ?? '';
                  const fieldError = formErrors.completionDates[content.id];
                  const checkboxId = `content-${content.id}`;
                  const dateInputId = `completion-${content.id}`;
                  const parsedDate = completionDate
                    ? new Date(`${completionDate}T00:00:00`)
                    : undefined;
                  const selectedDate =
                    parsedDate && !Number.isNaN(parsedDate.getTime())
                      ? parsedDate
                      : undefined;
                  const errorMessageId = fieldError
                    ? `${dateInputId}-error`
                    : undefined;

                  return (
                    <div
                      key={content.id}
                      className={cn(
                        'rounded-2xl border border-border/70 bg-background p-4 shadow-xs transition-colors hover:border-primary/40 hover:shadow-sm',
                        isSelected &&
                          'border-primary/50 bg-primary/5 shadow-sm',
                      )}
                    >
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-1 items-start gap-3">
                          <Checkbox
                            id={checkboxId}
                            checked={isSelected}
                            onCheckedChange={() =>
                              handleToggleContent(content.id)
                            }
                            className="mt-1 h-5 w-5 rounded-md"
                          />
                          <div className="space-y-1">
                            <Label
                              htmlFor={checkboxId}
                              className="text-sm font-semibold text-foreground"
                            >
                              {content.title}
                            </Label>
                            <p className="text-xs text-muted-foreground">
                              {content.courseTitle}
                            </p>
                          </div>
                        </div>

                        {isSelected ? (
                          <div className="flex flex-col gap-2 ml-8">
                            <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                              Completion Date (required)
                              <span className="ml-1 text-destructive">*</span>
                            </p>
                            <Popover
                              open={activeCalendarId === content.id}
                              onOpenChange={(open) =>
                                setActiveCalendarId(open ? content.id : null)
                              }
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  id={dateInputId}
                                  variant="outline"
                                  aria-invalid={Boolean(fieldError)}
                                  aria-describedby={errorMessageId}
                                  className={cn(
                                    'inline-flex h-10 w-full items-center justify-between gap-2 rounded-xl border border-border/60 bg-background px-3 text-left text-sm font-normal shadow-none transition-colors hover:border-primary/50 focus-visible:border-primary/60 focus-visible:ring-primary/30',
                                    !completionDate && 'text-muted-foreground',
                                  )}
                                >
                                  <span className="flex items-center gap-2">
                                    <CalendarDays className="h-4 w-4" />
                                    {selectedDate
                                      ? format(selectedDate, 'PPP')
                                      : 'Select completion date'}
                                  </span>
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-auto p-0"
                                align="end"
                              >
                                <Calendar
                                  mode="single"
                                  selected={selectedDate}
                                  onSelect={(date) => {
                                    if (!date) {
                                      handleDateChange(content.id, '');
                                      return;
                                    }
                                    setActiveCalendarId(null);
                                    handleDateChange(
                                      content.id,
                                      format(date, 'yyyy-MM-dd'),
                                    );
                                  }}
                                  initialFocus
                                  captionLayout="dropdown"
                                />
                              </PopoverContent>
                            </Popover>
                            {fieldError ? (
                              <p
                                id={errorMessageId}
                                className="text-xs text-destructive"
                              >
                                {fieldError}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="space-y-2.5">
            <Label
              htmlFor="additional-info"
              className="text-sm font-semibold text-foreground"
            >
              Additional Information (optional)
            </Label>
            <Textarea
              id="additional-info"
              value={additionalInfo}
              onChange={(event) => setAdditionalInfo(event.target.value)}
              maxLength={500}
              placeholder="Any additional details about your course completion..."
              className="min-h-[100px] rounded-2xl border border-border/60 bg-background px-3.5 py-3 text-sm shadow-none focus-visible:border-primary/60 focus-visible:ring-primary/30"
            />
            <div className="text-right text-xs text-muted-foreground">
              {additionalInfo.length}/500
            </div>
          </section>
        </CardContent>
        <CardFooter className="border-t border-border/60 bg-muted/20 p-5">
          <Button
            type="submit"
            className="w-full gap-2"
            disabled={!isFormValid || createCompletionRequest.isPending}
          >
            {createCompletionRequest.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Submit Request
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
};
